import {inject} from '@angular/core';
import {CanActivateFn} from '@angular/router';
import {map, take} from 'rxjs';
import {environment} from '../../../environments/environment';
import {AuthService} from '../services/auth.service';

/**
 * Protects all /app/* routes.
 *
 * If the user is not authenticated, they are sent to the npr_auth app
 * (/login) with the current URL as a ?redirect= parameter so npr_auth
 * can bounce them back here after a successful login.
 *
 * If authenticated but email is not verified, they are sent to verify-email
 * in npr_auth instead of the portal.
 */
export const authGuard: CanActivateFn = () => {
  const auth = inject(AuthService);

  const redirectToLogin = () => {
    const returnUrl = window.location.href;
    window.location.href = `${environment.authUrl}/login?redirect=${encodeURIComponent(returnUrl)}`;
    return false;
  };

  const redirectToVerifyEmail = (user: NonNullable<ReturnType<typeof auth.user>>) => {
    window.location.href = auth.verifyEmailUrl(user);
    return false;
  };

  const allowOrRedirect = (user: ReturnType<typeof auth.user>) => {
    if (!user) return redirectToLogin();
    if (!auth.isEmailVerified(user)) return redirectToVerifyEmail(user);
    return true;
  };

  // Fast path: session already resolved.
  if (auth.ready()) {
    return allowOrRedirect(auth.user());
  }

  // Slow path: wait for ensureSession() on first page load.
  return auth.ensureSession().pipe(
    take(1),
    map((user) => allowOrRedirect(user))
  );
};
